import React, { useCallback, useState, ReactElement } from 'react';
import Card from '../../../components/card/card.component';
import FieldText from '../../../components/field-text/field-text.component';
import Button from '../../../components/button/button.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import ModalLabel from '../../../components/modal-label/modal-label.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import ModalFieldBlock from '../../../components/modal-field-block/modal-field-block.component';
import ErrorBlock from '../../../components/error-block/error-block.component';
import Loader from '../../../components/loader/loader.component';

const DealTaskTemplateSaveModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isOpen,
    isSaving,
    dealTasksData,
    errorMessage,
    onSaveForm,
    onCloseModal,
  } = props;

  const [dealTemplateTitleValue, setDealTemplateTitleValue]: [string, Function] = useState('');
  const [dealTemplateDescriptionValue, setDealTemplateDescriptionValue]: [string, Function] = useState('');

  const isSaveDisabled: boolean = !dealTemplateTitleValue;

  const closeModal: Function = useCallback((): void => {
    if (onCloseModal) {
      onCloseModal();
    }

    setDealTemplateTitleValue('');
    setDealTemplateDescriptionValue('');
  }, []);

  const onTitleChange: Function = useCallback((title: string): void => {
    setDealTemplateTitleValue(title);
  }, []);

  const onDescriptionChange: Function = useCallback((event: React.ChangeEvent, descriptionValue: string): void => {
    setDealTemplateDescriptionValue(descriptionValue);
  }, []);

  const getDealTemplateInitialDataFromDealTasksData: Function = useCallback((
    dealTaskGroupList: DealTaskGroupInput[],
  ): DealTemplateInitialDataInput => ({
    dealTaskGroups: dealTaskGroupList.map((
      dealTaskGroupItem: DealTaskGroupInput, dealTaskGroupIndex: number,
    ): DealTaskGroupInput => ({
      id: dealTaskGroupIndex + 1,
      name: dealTaskGroupItem.name,
      tasks: dealTaskGroupItem.tasks && dealTaskGroupItem.tasks.length
        ? dealTaskGroupItem.tasks.map((dealTask: DealTaskInput, dealTaskIndex: number): DealTaskInput => ({
          id: dealTaskIndex + 1,
          name: dealTask.name,
          subTasks: dealTask.subTasks && dealTask.subTasks.length
            ? dealTask.subTasks.map((dealSubTask: DealTaskInput, dealSubTaskIndex: number): DealTaskInput => ({
              id: dealSubTaskIndex + 1,
              name: dealSubTask.name,
            })) : [],
        })) : [],
    })),
  }), [dealTasksData]);

  const saveForm: MouseEventHandlerFunction = useCallback((): void => {
    if (!dealTemplateTitleValue && (!dealTasksData || !dealTasksData.length)) {
      return;
    }

    if (onSaveForm) {
      const dealTemplateInitialData: DealTemplateInitialDataInput = getDealTemplateInitialDataFromDealTasksData(
        dealTasksData,
      );

      onSaveForm(dealTemplateTitleValue, dealTemplateDescriptionValue, dealTemplateInitialData, (): void => {
        setDealTemplateTitleValue('');
        setDealTemplateDescriptionValue('');
      });
    }
  }, [dealTemplateTitleValue, dealTemplateDescriptionValue, dealTasksData]);

  return (
    <ModalLayout isOpen={isOpen} title="Save Due Diligence Template" onCloseModal={closeModal}>
      <Card isContainer>
        <ModalLabel>
          Template name:
        </ModalLabel>
        <ModalFieldBlock>
          <FieldText
            isPlain
            isBordered
            value={dealTemplateTitleValue}
            onChange={(event: React.ChangeEvent): void => {
              onTitleChange((event.target as HTMLInputElement).value);
            }}
          />
        </ModalFieldBlock>
        <ModalLabel>
          Description:
        </ModalLabel>
        <ModalFieldBlock>
          <FieldText
            isPlain
            isBordered
            isMultiline
            value={dealTemplateDescriptionValue}
            onChange={(event: React.ChangeEvent, value: string): void => {
              onDescriptionChange(event, value);
            }}
          />
        </ModalFieldBlock>
      </Card>
      <ModalButtonBlock>
        <Button isActive isDisabled={isSaveDisabled} onClick={saveForm}>
          Save
        </Button>
        {
          isSaving && (
            <Loader isInline isModal isSmall />
          )
        }
      </ModalButtonBlock>
      {
        !!errorMessage && (
          <ErrorBlock errorMessage={errorMessage} />
        )
      }
    </ModalLayout>
  );
});

export default DealTaskTemplateSaveModal;
